import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import setInitialValues from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetInitialValues";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import {
    getCurrencies,
    getFulfillmentLabel,
    getIsPunchOutSession,
    getLanguages,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setCurrency from "@insite/client-framework/Store/Context/Handlers/SetCurrency";
import setLanguage from "@insite/client-framework/Store/Context/Handlers/SetLanguage";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getHeader, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconProps, IconWrapper } from "@insite/mobius/Icon";
import Globe from "@insite/mobius/Icons/Globe";
import MapPin from "@insite/mobius/Icons/MapPin";
import Menu from "@insite/mobius/Icons/Menu";
import User from "@insite/mobius/Icons/User";
import { MappedLink } from "@insite/mobius/Menu";
import PanelMenu, { PanelMenuPresentationProps } from "@NA/MobiusOverrides/PanelMenu";
import PanelRow, { PanelRowPresentationProps } from "@insite/mobius/PanelMenu/PanelRow";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import omitSingle from "@insite/mobius/utilities/omitSingle";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import styled, { css } from "styled-components";
import ADIColors from "@NA/Utils/ADIColors";
import Hidden from "@insite/mobius/Hidden";
import Drawer, { DrawerPresentationProps } from "@NA/Components/ADIDrawer";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import toggleLoginDrawer from "@NA/Handlers/ADILoginDrawerHandler";
import { checkVaildUserForHmiRewards, getMenuItems } from "@NA/Utils/Utility";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import MyAccountPanelMenu from "@NA/Components/ADIMyAccountPanelMenu";
import { CustomCategoryModel } from "@insite/client-framework/Types/ApiModels";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { StyledButton } from "@insite/mobius/Clickable";
import Link from "@insite/mobius/Link";
import { LinksIdArray, LinksIdModel } from "../Widgets/Header/HeaderLinkList";
import { MaNavSettingSettings } from "@NA/Store/CustomTypeModel/CustomSettingCollectionModel";
import toggleRewardsDrawer from "@NA/Handlers/ADIRewardsDrawerHandler";

const mapStateToProps = (state: CustomApplicationState, OwnProps: OwnProps) => {
    const headerLinkListLinkFields =
        (getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
            return widget.type === "Header/HeaderLinkList";
        })?.fields.links as LinksIdModel[]) ?? [];
    const cart = getCurrentCartState(state);
    const billToState = getBillToState(state, cart?.value?.billToId);
    const shipToState = getShipToState(state, cart?.value?.shipToId);
    const userRoles = state.context.session.userRoles;
    const loyaltyState = state.ADIGlobalData?.loyaltyRewardStore;
    const showMyAccMenuItems = state.context.settings.settingsCollection.maNavSettingSettings;
    const shopMenuLink = OwnProps.links.find(item => item.title === "Shop");
    const allCategories = shopMenuLink?.children?.map(item => getCategoryState(state, item.categoryId).value);
    const show = state.context.settings.settingsCollection.maNavSettingSettings;
    return {
        currencies: getCurrencies(state),
        currentCurrencyId: state.context.session.currency?.id,
        currentCurrencySymbol: state.context.session.currency?.currencySymbol,
        languages: getLanguages(state),
        currentLanguage: state.context.session.language,
        currentLocation: getLocation(state),
        isSigningIn: state.context.isSigningIn,
        userName: state.context.session?.userName,
        firstName: state.context.session?.firstName,
        showMyAccMenuItems,
        billToState: billToState,
        shipToState: shipToState,
        isGuest: state.context.session?.isGuest,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage"),
        headerLinkListLinkFields,
        showCustomerMenuItem: getSettingsCollection(state).accountSettings.enableWarehousePickup,
        fulfillmentLabel: getFulfillmentLabel(state),
        drawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,
        isPunchOutSession: getIsPunchOutSession(state),
        userRoles,
        loyaltyState,
        allCategories: allCategories as unknown as CustomCategoryModel[],
        session: state.context.session,
        show,
    };
};

export interface MappedLinkCustom extends MappedLink {
    children?: MappedLinkCustom[];
    properties?: any;
    newProductCategories?: CustomCategoryModel[] | null;
    categoryId?: string;
    shopProductSubCat?: CustomCategoryModel[] | null;
}

const mapDispatchToProps = {
    setCurrency,
    setLanguage,
    signOut,
    cancelPunchOut,
    setInitialValues,
    setNavDrawerIsOpen,
    toggleLoginDrawer,
    toggleRewardsDrawer
};

interface OwnProps {
    links: MappedLinkCustom[];
    showQuickOrder: boolean;
    quickOrderLink: PageLinkModel | undefined;
    newProductCategory: CustomCategoryModel | undefined;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface NavigationDrawerStyles {
    menuTriggerButton?: ButtonPresentationProps;
    drawer?: DrawerPresentationProps;
    drawerSectionWrapper?: InjectableCss;
    panelMenu?: PanelMenuPresentationProps;
    myAccountPanelMenu?: PanelMenuPresentationProps;
    panelSectionWrapper?: InjectableCss;
    menuRowIcon?: IconProps;
    userRowTypography?: TypographyPresentationProps;
    userNumberTypography?: TypographyPresentationProps;
    punchOutUserRowText?: TypographyPresentationProps;
    mainNavigationRow?: PanelRowPresentationProps;
    mainNavigationRowNew?: InjectableCss;
    mainNavigationRowIcon?: IconProps;
    punchOutNavigationRowIcon?: IconProps;
    mainNavigationRowTypography?: TypographyPresentationProps;
    logoLinks?: PanelRowPresentationProps & { typographyProps: TypographyPresentationProps };
    logoLinksHeaderLinks?: PanelRowPresentationProps & { typographyProps: TypographyPresentationProps };
    currencySymbol?: TypographyPresentationProps;
    changeCustomerRow?: PanelRowPresentationProps;
    changeCustomerRowContainer?: GridContainerProps;
    fulfillmentMethodGridItem?: GridItemProps;
    addressesGridItem?: GridItemProps;
    pickUpAddressGridItem?: GridItemProps;
    applyButtonGridItem?: GridItemProps;
    signOutRow?: PanelRowPresentationProps;
    signOutRowText?: TypographyPresentationProps;
    newProductLinkContainer?: InjectableCss;
    myBranchLinkContainer?: InjectableCss;
    headerButtonOutlineStyle?: InjectableCss
}

const StyledSection = getStyledWrapper("section");
const StyledSpan = getStyledWrapper("span");

export const navigationDrawerStyles: NavigationDrawerStyles = {
    menuTriggerButton: {
        css: css`
            cursor: pointer;
            color: ${ADIColors.darkBlue1};
        `,
    },
    headerButtonOutlineStyle: {
        css: css`
         :focus-within{
           outline: 1px solid #2d2926 !important;
         }
       `
    },
    drawer: {
        size: 300,
        cssOverrides: {
            drawerContent: css`
                overflow-x: hidden;
            `,
        },
    },
    drawerSectionWrapper: {
        css: css`
            &:first-child {
                margin-top: 0;
                padding-bottom: 5px;
            }
            &:last-child {
                margin-bottom: 5px;
            }
            margin-top: 5px;
            background-color: transparent;
        `,
    },
    panelMenu: {
        cssOverrides: {
            wrapper: css`
            
                > button {
                    width: 100%;  
                    padding:10px 20px;                 
                    span[class^="IconWrapper"]{
                        color:#002856;
                        > svg {
                            width:18px;
                            height:20px;
                            stroke-width:3;
                        }
                    }
                }
                div[class^="PanelChildren"]{
                    div[class^="PanelMenuWrapper"]{
                    > button {                                    
                    span[class^="IconWrapper"]{
                        color:rgb(45,41,38);
                        line-height: 17px;
                        font-size: 12px;
                        height: 38px;
                        > svg {
                            width:18px;
                            height:20px;
                            stroke-width:2;
                        }
                    }
                }
                }
                }
            `,
        },
    },
    myAccountPanelMenu: {
        cssOverrides: {
            wrapper: css`
                > button {
                    width: 100%;
                    // background: ${ADIColors.gray6};
                }
            `,
            menu: css`
                background: ${ADIColors.white};
                a,
                button {
                    background: ${ADIColors.white};
                    padding: 5px 20px;
                    span {
                        color: ${getColor("text.main")};
                        font-size: 12px;
                        font-weight: 600;
                    }
                }
            `,
        },
    },
    panelSectionWrapper: {
        css: css`
            height: 22px;
            display: flex;
            align-items: center;
        `,
    },
    menuRowIcon: {
        size: 22,
        css: css`
            margin-top: -2px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    userRowTypography: {
        color: "common.backgroundContrast",
        css: css`
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            font-size: 14px;
            font-weight: 600;
            font-stretch: normal;
            font-style: normal;
            line-height: 1.43;
            letter-spacing: normal;
            color: #fff;
        `,
    },
    userNumberTypography: {
        css: css`
            vertical-align: top;
            font-size: 11px;
            font-weight: 600;
            display: inline-block;
            padding-top: 3px;
            padding-left: 5px;
        `,
    },
    punchOutUserRowText: {
        transform: "uppercase",
        color: "common.backgroundContrast",
    },
    mainNavigationRow: {
        css: css`
            margin: 0px;
           padding: 15px 20px 10px 20px;
        `,
    },
    mainNavigationRowNew: {
        css: css`
            background-color: ${ADIColors.newPrimary} !important;
            margin: 0px;
            padding: 15px 20px 10px 20px;
            span {
                color: ${ADIColors.white} !important;
            }
        `,
    },
    mainNavigationRowIcon: {
        src: "User",
        color: "common.backgroundContrast",
        size: 22,
        css: css`
            margin-top: -2px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    punchOutNavigationRowIcon: {
        src: "User",
        color: "common.backgroundContrast",
        size: 22,
        css: css`
            margin-top: -2px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    mainNavigationRowTypography: {
        transform: "uppercase",
        color: "common.backgroundContrast",
        css: css`
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            font-size: 14px !important;
            font-weight: 600;
            line-height: 1.43 !important;
            letter-spacing: normal;
            color: #2d2926 !important;
        `,
    },
    logoLinks: {
        color: "common.accent",
        typographyProps: {
            ellipsis: true,
            transform: "uppercase",
        },
        css: css`
            margin: 2px;
        `,
    },
    logoLinksHeaderLinks: {
        typographyProps: {
            ellipsis: true,
        },
        css: css`
            margin: 2px;
            font-size: 12px;
            color: ${getColor("text.main")} !important;
            font-weight: bold;
            &:visited,
            &:active {
                color: ${getColor("text.main")} !important;
            }
        `,
    },
    currencySymbol: {
        size: 22,
        css: css`
            margin-top: -4px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    changeCustomerRow: {
        css: css`
            &:focus {
                outline: none;
            }
        `,
    },
    changeCustomerRowContainer: { gap: 15 },
    fulfillmentMethodGridItem: { width: 12 },
    addressesGridItem: { width: 12 },
    pickUpAddressGridItem: { width: 12 },
    applyButtonGridItem: { width: 12 },
    signOutRowText: {
        transform: "uppercase",
    },
    signOutRow: {
        css: css`
            width: 100%;
        `,
    },
    newProductLinkContainer: {
        css: css`
            margin-top: 20px;
            font-size: 12px;
            font-weight: bold;
            position: relative;
            top: 10px;

            > ${StyledButton} {
                background-color: ${ADIColors.white};
                span {
                    text-transform: capitalize;
                }
            }
        `,
    },
    myBranchLinkContainer: {
        css: css`
            font-size: 12px;
            font-weight: bold;
            margin-bottom: 5px;
            padding:0px 20px;

            > ${StyledButton} {
                background-color: ${ADIColors.white};
                font-size: 12px;
                font-weight: bold;
                border-bottom: 2px solid #ddd;
                padding:30px 0px;
                span {
                    font-size: 12px !important;
                    font-weight: bold !important;
                    line-height: normal !important;
                    letter-spacing: normal;
                    text-transform: capitalize;
                }
            }
        `,
    },
};

const styles = navigationDrawerStyles;

const DrawerHeaderButtonWrapper = styled.div<any>`
    padding: 0px;
    display: flex;
    align-items: center;
    gap: 13px;
   `;

const NavigationDrawer: FC<Props> = props => {
    const linksVal = props.headerLinkListLinkFields.map(fields => fields.fields);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const openDrawer = () => {
        props.toggleLoginDrawer({ loginDrawer: false });
        props.setNavDrawerIsOpen({ navDrawerIsOpen: true });
        props.setInitialValues({});
        props.toggleRewardsDrawer({ rewardsDrawer: false });
        setTimeout(() => {
            setIsInitialLoad(false);
        }, 500);
    };

    const closeDrawer = () => {
        props.setNavDrawerIsOpen({ navDrawerIsOpen: false });
        setIsInitialLoad(true);
        setTimeout(() => props.setNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);
    };

    const onSignOutHandler = () => {
        props.setNavDrawerIsOpen({ navDrawerIsOpen: false });
        if (window) {
            window.sessionStorage.removeItem("adi_pricespider_webtoken");
            window.sessionStorage.removeItem("redirectAfterLogin");
        }
        props.isPunchOutSession ? props.cancelPunchOut() : props.signOut();
    };

    const changeCustomerMethod = () => {
        history.push("/MyAccount/ChangeCustomer");
        closeDrawer();
    };

    const {
        quickOrderLink,
        links,
        showQuickOrder,
        currencies,
        currentCurrencyId,
        currentCurrencySymbol,
        languages,
        currentLanguage,
        setLanguage,
        setCurrency,
        userName,
        firstName,
        billToState,
        shipToState,
        isGuest,
        myAccountPageLink,
        signInUrl,
        currentLocation,
        showCustomerMenuItem,
        fulfillmentLabel,
        isPunchOutSession,
        headerLinkListLinkFields,
        history,
        userRoles,
        loyaltyState,
        allCategories,
        newProductCategory,
        session,
        showMyAccMenuItems,
        show,
    } = props;

    const isAuthenticated = (session.isAuthenticated || session.rememberMe) && !session.isGuest;
    const currentPageUrl = currentLocation.pathname;
    // let myAccountLinks = Object.assign([], myAccountPageLink?.children ? myAccountPageLink?.children : []);
    let myAccountLinks = Object.assign(
        [],
        myAccountPageLink?.children
            ? myAccountPageLink.children.map(child => {
                return {
                    ...child,
                    title: child.title?.trim(),
                };
            })
            : [],
    );
    const dispatch = useDispatch();

    let roles: Array<string> = [];

    roles = getMenuItems(showMyAccMenuItems);

    const isProRewardsUser = userRoles && userRoles.includes("ADIProRewards");
    if (myAccountLinks && isProRewardsUser && checkVaildUserForHmiRewards(loyaltyState.loyalty)) {
        roles.push("ADI Pro Rewards");
        const accountIndex = myAccountLinks.findIndex((item: any) => item.title.toLowerCase() == "account");
        const proRewardsObject = {
            children: [],
            excludeFromNavigation: false,
            id: "122",
            parentId: "32",
            title: "ADI Pro Rewards",
            type: "AccountSettingsPage",
            url: "/myaccount",
        };
        myAccountLinks.splice(accountIndex + 1, 0, proRewardsObject);
    }

    const roleBasedMenuItems = myAccountLinks.filter((item: any) => {
        const data = roles.includes(item.title.trim()) ? item : null;
        return data;
    });
    const defaultLinks = linksVal.filter((link: any) => link.authentication === "Default");
    const conditionalLinks = linksVal.filter(
        link => link.authentication !== "Default" && link.authentication !== undefined,
    );

    const renderLink = (link: LinksIdArray) => {
        const target = link.openInNewWindow ? "_blank" : "";
        switch (link.authentication) {
            case "Unauthenticated":
                return !isAuthenticated ? (
                    <PanelRow
                        style={{ margin: "0px", backgroundColor: "transparent" }}
                        key={`${link.title}`}
                        {...(styles.logoLinksHeaderLinks && omitSingle(styles.logoLinksHeaderLinks, "typographyProps"))}
                        isCurrent={currentPageUrl === link.redirectUrl}
                        onClick={closeDrawer}
                        onKeyPress={(event: React.KeyboardEvent) => {
                            if (event.key === " " && history && link?.redirectUrl) {
                                event.preventDefault();
                                closeDrawer();
                                history.push(link.redirectUrl);
                            }
                        }}
                        href={link.redirectUrl}
                        target={target}
                    >
                        <Typography {...styles.logoLinksHeaderLinks?.typographyProps}>{link.title}</Typography>
                    </PanelRow>
                ) : null;
            case "Authenticated":
                return isAuthenticated ? (
                    <PanelRow
                        style={{ margin: "0px", backgroundColor: "transparent" }}
                        key={`${link.title}`}
                        {...(styles.logoLinksHeaderLinks && omitSingle(styles.logoLinksHeaderLinks, "typographyProps"))}
                        isCurrent={currentPageUrl === link.redirectUrl}
                        onClick={closeDrawer}
                        onKeyPress={(event: React.KeyboardEvent) => {
                            if (event.key === " " && history && link?.redirectUrl) {
                                event.preventDefault();
                                closeDrawer();
                                history.push(link.redirectUrl);
                            }
                        }}
                        href={link.redirectUrl}
                        target={target}
                    >
                        <Typography {...styles.logoLinksHeaderLinks?.typographyProps}>{link.title}</Typography>
                    </PanelRow>
                ) : null;
            default:
                return (
                    <PanelRow
                        style={{ margin: "0px", backgroundColor: "transparent" }}
                        key={`${link.title}`}
                        {...(styles.logoLinksHeaderLinks && omitSingle(styles.logoLinksHeaderLinks, "typographyProps"))}
                        isCurrent={currentPageUrl === link.redirectUrl}
                        onClick={closeDrawer}
                        onKeyPress={(event: React.KeyboardEvent) => {
                            if (event.key === " " && history && link?.redirectUrl) {
                                event.preventDefault();
                                closeDrawer();
                                history.push(link.redirectUrl);
                            }
                        }}
                        href={link.redirectUrl}
                        target={target}
                    >
                        <Typography {...styles.logoLinksHeaderLinks?.typographyProps}>{link.title}</Typography>
                    </PanelRow>
                );
        }
    };

    return (
        <>
            <DrawerHeaderButtonWrapper>
                <ButtonIcon size={32} {...styles.menuTriggerButton} src={Menu} onClick={openDrawer} />
                <Hidden above="sm" {...styles.headerButtonOutlineStyle} style={{ display: "flex" }}>
                    <Link href="/" style={{ lineHeight: "0px" }}>
                        <img
                            width="56px"
                            alt="ADI a Resideo company"
                            src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAKkAAABHBAMAAACOtXruAAAABGdBTUEAALGPC/xhBQAAAAFzUkdCAK7OHOkAAAAqUExURUdwTAAoVgAoVgAoVQAoVQAoVAAnVQAoVQAgUAApVgAoVgAnVwAoVQAoVqZF9jgAAAANdFJOUwDfgiKfQFzDEO+gcLA7J3xLAAAEFUlEQVRYw6WYz4vTQBTHp4amS3YPkb0oGFh/gAcdqCjqJVBw/4CCVwsFZS9SWJBlLwYievEgiBcXtAf/gMJeBC+F/Qf20O7ablnzv5g0ncybNy9vis6laTPzSfq+ed/3JkKYY5StxgydEDIzxtW3957jKcIrz4X0z8QZTC3G9y6a49Nr/WrFzhrUrC1SY06DpjbAAjd1LsSPl3BOTFMHasG5WIM6yyM2uW2rgqmJWvBbbLSd1LCI2ATEKiKpUCz/xEntLCM2aWNVEDWoFvRF48JJbYte8TFVU7ZoaqzmL/IFUxd1rgL5Bi1H1DEQa7RIHdSZipiKwYCmRmrBWbHg2EENq0Bum8vDmhzYL473HdSOaGXwZj06L1tQrOLp4qltrcM1Q2uT2lM/T5YxmvPUOfCiuZGYIW1YuVin+UfKUmdiY1h9OYY3FdI5cFYe77DUEOiQr6ij6hw4KY+PWGoH6LAMAU3VhtUtjy9J6mIvH6/vJ0CsMgQ0dWCKheSS4CzWofxfNDUC1SVZ3TNLTQB1WkNtYrHMPCCom4A6qaEGQIdAK8tQxQOA7dLU2E6acwfVi+AjQVJhoqyEg7ZFUeHNHpHUDShWZFdakqq1yF2DovogaarJnx1UkBtTktqwxTJsi6Z+BNlFUQeUw81d1CvVqgVJTQBJgueFp4LAtgmqlxFiQbloKpjaJ6gB7XBHLqr2gmOCGoPq0jLSm6eO9d8iqKRYhQYOql7XIagRbAVAevcd1Jij+iBHQDkCtvUv1BYtFsiDf6HGZiugx5//oVahzG2qZxS9lKf2GKrOASSWzgPnM2A/WZt2dcF5UEOVTBY0zL4Njkueespk7KBOLG1bNdQh4y4JbgWMMreeZ1lO2IR5nyDqPkcNGNcO6sXKsguOGjMVJqaqC7Itmpow1XCE+zY4VuWbpOoCkxdOTI0YsdSug6T2mC7Dz6y+zZaLojaH0DIRtcGJpWyLoo657q3HiaXygKA+MXYJmDqiq4v5rsCmvoBztjHVy+y+De0ACOrjm9jaTGpAtwLItiS49N3Dw+vmlIm123hH9W1WHkj20jOLOuDFWpVvWVPYtLeb1MghVmlbktxnqQCkmOqTO1MrDyR36W2BqS0YnPcP9RgaeSCpltF49gxqXPe+TRpySUasmbCoI7v7wdZR2Jas7NYSa9G3qF5W976tZUgsK7G2yKiaVD+re9/WhOZZUbuWWPO2TW1kxNYC189ZRZ3YYnWETR2s83JUC2+LdSAIalInlhCfYB5I5cBIrG+CoHpmfhgDyHKiqFisLylFDbL6l6NNWL4lKdYB9SSGYM6ZRQXRma6oplhffwmaOrZ7daouLVKperlKrBs/U1FDvbOrRt+mfqhO7nZfLT+eCa/8fmvvKZ79SM3d+QtPd85RhsUwmgAAAABJRU5ErkJggg=="
                        />
                    </Link>
                </Hidden>
                <VisuallyHidden>{translate("menu")}</VisuallyHidden>
            </DrawerHeaderButtonWrapper>
            <Drawer
                draggable
                position="left"
                {...styles.drawer}
                isOpen={props.drawerIsOpen}
                handleClose={closeDrawer}
                contentLabel="menu drawer"
            >
                <StyledSection {...styles.drawerSectionWrapper}>
                    <Hidden above="sm">
                        {userName && !isGuest && !isPunchOutSession ? (
                            <MyAccountPanelMenu
                                style={{ color: ADIColors.white }}
                                currentUrl={currentPageUrl}
                                panelTrigger={
                                    <PanelRow hasChildren {...styles.mainNavigationRowNew}>
                                        <StyledSpan {...styles.panelSectionWrapper}>
                                            <Typography {...styles.userRowTypography}>
                                                Hi, {firstName} |{" "}
                                                <Typography {...styles.userNumberTypography}>
                                                    {billToState?.value?.customerNumber && (
                                                        <>{billToState?.value?.customerNumber}</>
                                                    )}
                                                </Typography>
                                            </Typography>
                                        </StyledSpan>
                                    </PanelRow>
                                }
                                menuItems={roleBasedMenuItems}
                                dispatch={dispatch}
                                maxDepth={1}
                                closeOverlay={closeDrawer}
                                layer={0}
                                billToState={billToState}
                                shipToState={shipToState}
                                userName={userName}
                                isPunchOutSession={isPunchOutSession}
                                currentUserIsGuest={isGuest}
                                onSignOutHandler={onSignOutHandler}
                                changeCustomerMethod={changeCustomerMethod}
                                show={show}
                                displayChangeCustomerLink={session.displayChangeCustomerLink}
                                {...styles.myAccountPanelMenu}
                            />
                        ) : isPunchOutSession ? (
                            <PanelRow {...styles.mainNavigationRow}>
                                <StyledSpan {...styles.panelSectionWrapper}>
                                    <Icon {...styles.punchOutNavigationRowIcon} />
                                    <Typography {...styles.punchOutUserRowText}>{userName}</Typography>
                                </StyledSpan>
                            </PanelRow>
                        ) : (
                            <PanelRow
                                {...styles.mainNavigationRow}
                                isCurrent={currentPageUrl === signInUrl?.url}
                                style={{
                                    backgroundColor: ADIColors.newPrimary,
                                    fontWeight: "bold",
                                    padding: "15px 20px",
                                    width: "100%",
                                }}
                                onClick={() => {
                                    closeDrawer();
                                    props.toggleLoginDrawer({ loginDrawer: true });
                                }}
                                onKeyPress={() => {
                                    closeDrawer();
                                    props.toggleLoginDrawer({ loginDrawer: true });
                                }}
                            >
                                <StyledSpan {...styles.panelSectionWrapper}>

                                    <Typography {...styles.userRowTypography} style={{ color: ADIColors.white }}>
                                        {translate("Sign In")}
                                    </Typography>
                                </StyledSpan>
                            </PanelRow>
                        )}
                    </Hidden>
                    {/* covers MainNavigation functionality */}

                    {links.map((link, index) => {
                        const isSolutionandServices = link && link.title === "Solutions & Services";

                        let shopChildrenItems: MappedLinkCustom[] = [];

                        if (link.title === "Shop") {
                            const customShopMenu = link.children?.map(child => {
                                let shopAll = {} as unknown as MappedLinkCustom;
                                let dealsLink = {} as unknown as MappedLinkCustom;
                                let newProductLink = {} as unknown as MappedLinkCustom;

                                shopAll = {
                                    categoryId: child.url,
                                    children: [],
                                    properties: {},
                                    title: `${translate("Shop All {0}", child.title)}`,
                                    url: child.url,
                                };

                                if (child.properties["newProductsLink"]) {
                                    newProductLink = {
                                        categoryId: "",
                                        children: [],
                                        properties: {},
                                        title: `${translate("New Products")}`,
                                        url: child.properties["newProductsLink"],
                                    };
                                }

                                if (child.properties["dealsLink"]) {
                                    dealsLink = {
                                        categoryId: "",
                                        children: [],
                                        properties: {},
                                        title: `${translate("Deals")}`,
                                        url: child.properties["dealsLink"],
                                    };
                                }

                                const shopChildrenL3 = child?.children?.map((item: MappedLinkCustom) => {
                                    let shopAllL3 = {} as unknown as MappedLinkCustom;

                                    shopAllL3 = {
                                        categoryId: item.categoryId,
                                        children: [],
                                        properties: {},
                                        title: `${translate("Shop All {0}", item.title)}`,
                                        url: item.url,
                                    };

                                    const customMenu = [shopAllL3, ...(item.children || [])];

                                    return {
                                        ...item,
                                        children: item.children?.length ? customMenu : item.children || [],
                                    };
                                });

                                let MenuItems: MappedLinkCustom[] = [
                                    shopAll,
                                    newProductLink,
                                    dealsLink,
                                    ...(shopChildrenL3 || []),
                                ].filter(obj => Object.keys(obj).length > 0);

                                return {
                                    ...child,
                                    children: MenuItems,
                                };
                            });

                            shopChildrenItems = customShopMenu || [];
                        }

                        if (link.children && link.children.length > 0 && isInitialLoad) {
                            return (
                                <PanelMenu
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={`${index}${link.title}`}
                                    currentUrl={currentPageUrl}
                                    closeOverlay={closeDrawer}
                                    panelTrigger={
                                        <PanelRow title="Main Menu" hasChildren {...styles.mainNavigationRow}>
                                            <Typography {...styles.mainNavigationRowTypography}>
                                                {link.title}
                                            </Typography>
                                        </PanelRow>
                                    }
                                    menuItems={link.title === "Shop" ? shopChildrenItems : link.children}
                                    maxDepth={2}
                                    layer={0}
                                    {...styles.panelMenu}
                                />
                            );
                        }
                        if (link.children && link.children.length > 0 && !isInitialLoad) {
                            return (
                                <PanelMenu
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={`${index}${link.title}`}
                                    currentUrl={currentPageUrl}
                                    closeOverlay={closeDrawer}
                                    panelTrigger={
                                        <PanelRow title="Main Menu" hasChildren {...styles.mainNavigationRow}>
                                            <Typography {...styles.mainNavigationRowTypography}>
                                                {link.title}
                                            </Typography>
                                        </PanelRow>
                                    }
                                    menuItems={link.title === "Shop" ? shopChildrenItems : link.children}
                                    isSolutionandServices={isSolutionandServices}
                                    maxDepth={link.maxDepth || 3}
                                    layer={0}
                                    {...styles.panelMenu}
                                />
                            );
                        }
                        return (
                            <PanelRow
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                href={link.url}
                                target={link.openInNewWindow ? "_blank" : ""}
                                {...styles.mainNavigationRow}
                            >
                                <Typography {...styles.mainNavigationRowTypography}>{link.title}</Typography>
                            </PanelRow>
                        );
                    })}
                    {showQuickOrder && quickOrderLink && (
                        <PanelRow
                            isCurrent={currentPageUrl === quickOrderLink.url}
                            onClick={closeDrawer}
                            href={quickOrderLink.url}
                            {...styles.mainNavigationRow}
                        >
                            <Typography {...styles.mainNavigationRowTypography}>{quickOrderLink.title}</Typography>
                        </PanelRow>
                    )}

                    {newProductCategory?.path ? (
                        <StyledWrapper {...styles.newProductLinkContainer}>
                            <PanelRow
                                onClick={closeDrawer}
                                {...styles.mainNavigationRow}
                                href={newProductCategory?.path}
                            >
                                <Typography {...styles.mainNavigationRowTypography}>
                                    {translate("Shop New Products")}
                                </Typography>
                            </PanelRow>
                        </StyledWrapper>
                    ) : null}
                    <StyledWrapper {...styles.myBranchLinkContainer}>
                        <PanelRow onClick={closeDrawer} {...styles.mainNavigationRow} href={"myaccount/shopmybranch"}>
                            <Typography {...styles.mainNavigationRowTypography}>
                                {translate("Shop My Branch")}
                            </Typography>
                        </PanelRow>
                    </StyledWrapper>
                </StyledSection>

                {linksVal.length > 0 && (
                    <StyledSection {...styles.drawerSectionWrapper}>
                        {conditionalLinks.map(link => link.redirectUrl && renderLink(link))}
                        {defaultLinks.map(link => link.redirectUrl && renderLink(link))}
                    </StyledSection>
                )}
                <StyledSection {...styles.drawerSectionWrapper}>
                    <SelectorMenu
                        options={currencies?.map(c => {
                            return {
                                title: c.currencyCode.toUpperCase(),
                                clickableProps: {
                                    onClick: () => {
                                        setCurrency({ currencyId: c.id });
                                        closeDrawer();
                                    },
                                },
                            };
                        })}
                        closeModal={closeDrawer}
                        currentOption={currencies?.find(c => c.id === currentCurrencyId)?.currencyCode}
                        currentOptionIcon={<Typography {...styles.currencySymbol}>{currentCurrencySymbol}</Typography>}
                    />
                    <SelectorMenu
                        dataTestSelector="mobileLanguageSelector"
                        options={languages?.map(l => {
                            return {
                                title: l.languageCode.toUpperCase(),
                                clickableProps: {
                                    onClick: () => {
                                        setLanguage({ languageId: l.id });
                                        closeDrawer();
                                    },
                                },
                            };
                        })}
                        currentOption={currentLanguage?.languageCode}
                        closeModal={closeDrawer}
                        currentOptionIcon={
                            currentLanguage?.imageFilePath ? (
                                <LogoImage src={currentLanguage.imageFilePath} alt="" />
                            ) : (
                                <Icon src={Globe} {...styles.menuRowIcon} />
                            )
                        }
                    />
                    {showCustomerMenuItem && (
                        <PanelMenu
                            {...styles.panelMenu}
                            panelTrigger={
                                <PanelRow hasChildren {...styles.logoLinks} color="common.accent">
                                    <Typography {...styles.logoLinks?.typographyProps}>
                                        <Icon src={MapPin} {...styles.menuRowIcon} />
                                        {fulfillmentLabel}
                                    </Typography>
                                </PanelRow>
                            }
                            layer={0}
                            closeOverlay={closeDrawer}
                        >
                            {isPunchOutSession ? (
                                <PanelRow tabIndex={-1} {...styles.changeCustomerRow}>
                                    {createWidgetElement("Header/AddressDrawerPunchOutCustomer", {
                                        fields: {},
                                    })}
                                </PanelRow>
                            ) : (
                                <PanelRow tabIndex={-1} {...styles.changeCustomerRow}>
                                    <GridContainer {...styles.changeCustomerRowContainer}>
                                        <GridItem {...styles.fulfillmentMethodGridItem}>
                                            {createWidgetElement("Header/AddressDrawerFulfillmentMethodSelector", {
                                                fields: {},
                                            })}
                                        </GridItem>
                                        <GridItem {...styles.addressesGridItem}>
                                            {createWidgetElement("Header/AddressDrawerSelectCustomer", {
                                                fields: {},
                                            })}
                                        </GridItem>
                                        <GridItem {...styles.pickUpAddressGridItem}>
                                            {createWidgetElement("Header/AddressDrawerPickUpLocationSelector", {
                                                fields: {},
                                            })}
                                        </GridItem>
                                        <GridItem {...styles.applyButtonGridItem}>
                                            {createWidgetElement("Header/AddressDrawerApplyButton", { fields: {} })}
                                        </GridItem>
                                    </GridContainer>
                                </PanelRow>
                            )}
                        </PanelMenu>
                    )}
                </StyledSection>
            </Drawer>
        </>
    );
};

const LogoImage = styled.img`
    margin-right: 10px;
    height: 22px;
`;

interface SelectorMenuProps {
    options?: MappedLinkCustom[];
    closeModal?: () => void;
    currentOption: React.ReactNode;
    currentOptionIcon: React.ReactNode;
    dataTestSelector?: string;
}

const SelectorMenu: React.FC<SelectorMenuProps> = ({
    options,
    closeModal,
    currentOption,
    currentOptionIcon,
    dataTestSelector,
}) => {
    if (!options || options.length <= 1) {
        return null;
    }

    const trigger = (
        <PanelRow hasChildren {...styles.logoLinks}>
            <StyledSpan {...styles.panelSectionWrapper}>
                {currentOptionIcon}
                <Typography
                    data-test-selector={`${dataTestSelector}_currentOption`}
                    {...styles.logoLinks?.typographyProps}
                >
                    {currentOption}
                </Typography>
            </StyledSpan>
        </PanelRow>
    );

    return (
        <PanelMenu
            data-test-selector={dataTestSelector}
            closeOverlay={closeModal}
            panelTrigger={trigger}
            menuItems={options}
            maxDepth={1}
            layer={0}
            {...styles.panelMenu}
        />
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(NavigationDrawer));
